import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  KeyIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import userService from '../../services/userService';
import agencyService from '../../services/agencyService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

const UserForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { user: currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: []
  });

  // Validation schema
  const validationSchema = Yup.object({
    username: Yup.string()
      .required('Le nom d\'utilisateur est requis')
      .min(3, 'Minimum 3 caractères')
      .max(50, 'Maximum 50 caractères')
      .matches(/^[a-zA-Z0-9_]+$/, 'Caractères autorisés: lettres, chiffres et underscore'),
    email: Yup.string()
      .required('L\'email est requis')
      .email('Email invalide'),
    first_name: Yup.string()
      .required('Le prénom est requis')
      .min(2, 'Minimum 2 caractères'),
    last_name: Yup.string()
      .required('Le nom est requis')
      .min(2, 'Minimum 2 caractères'),
    phone: Yup.string()
      .matches(/^[0-9\s\-+()]*$/, 'Numéro de téléphone invalide'),
    password: isEditMode 
      ? Yup.string()
          .min(6, 'Minimum 6 caractères')
          .nullable()
      : Yup.string()
          .required('Le mot de passe est requis')
          .min(6, 'Minimum 6 caractères'),
    confirm_password: isEditMode
      ? Yup.string()
          .nullable()
          .oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre')
      : Yup.string()
          .required('Confirmez le mot de passe')
          .oneOf([Yup.ref('password'), null], 'Les mots de passe doivent correspondre'),
    agency_id: Yup.number()
      .required('L\'agence est requise'),
    role_id: Yup.number()
      .required('Le rôle est requis'),
    is_active: Yup.boolean()
  });

  // Initial form values
  const initialValues = {
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    password: '',
    confirm_password: '',
    agency_id: '',
    role_id: '',
    is_active: true
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        
        // Prepare data for API
        const userData = {
          username: values.username,
          email: values.email,
          first_name: values.first_name,
          last_name: values.last_name,
          phone: values.phone || null,
          agency_id: parseInt(values.agency_id),
          role_id: parseInt(values.role_id),
          is_active: values.is_active
        };

        // Only include password if provided (for edit) or required (for create)
        if (values.password && (!isEditMode || values.password !== '')) {
          userData.password = values.password;
        }

        if (isEditMode) {
          await userService.updateUser(id, userData);
          showSuccess('Utilisateur mis à jour avec succès');
        } else {
          await userService.createUser(userData);
          showSuccess('Utilisateur créé avec succès');
        }
        
        navigate('/users');
      } catch (error) {
        showError(error.response?.data?.message || 'Une erreur est survenue');
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    fetchData();
    if (isEditMode) {
      fetchUser();
    }
  }, [id]);

  useEffect(() => {
    if (formik.values.role_id) {
      fetchRolePermissions();
    }
  }, [formik.values.role_id]);

  const fetchData = async () => {
    try {
      // Fetch agencies
      const agenciesData = await agencyService.getAllAgencies();
      setAgencies(agenciesData);

      // Fetch roles
      const rolesData = await userService.getAllRoles();
      setRoles(rolesData);

      // Fetch all permissions
      const permissionsData = await userService.getAllPermissions();
      setAvailablePermissions(permissionsData);

    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const data = await userService.getUser(id);
      setUserData(data);
      
      // Set form values from fetched data
      Object.keys(data).forEach(key => {
        if (formik.values[key] !== undefined) {
          formik.setFieldValue(key, data[key]);
        }
      });

      // Fetch permissions for the role
      if (data.role_id) {
        fetchRolePermissions();
      }

    } catch (error) {
      showError('Erreur lors du chargement de l\'utilisateur');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async () => {
    try {
      const roleId = formik.values.role_id;
      if (roleId) {
        const role = roles.find(r => r.id === parseInt(roleId));
        if (role) {
          setPermissions(role.permissions || []);
        }
      }
    } catch (error) {
      console.error('Error fetching role permissions:', error);
    }
  };

  const handleCreateRole = async () => {
    try {
      const newRoleData = {
        name: newRole.name,
        description: newRole.description,
        permissions: newRole.permissions
      };

      const createdRole = await userService.createRole(newRoleData);
      
      // Refresh roles list
      const rolesData = await userService.getAllRoles();
      setRoles(rolesData);
      
      // Select the new role
      formik.setFieldValue('role_id', createdRole.id);
      
      setShowRoleModal(false);
      setNewRole({ name: '', description: '', permissions: [] });
      showSuccess('Rôle créé avec succès');
      
    } catch (error) {
      showError('Erreur lors de la création du rôle');
    }
  };

  const handleCancel = () => {
    navigate('/users');
  };

  const PermissionBadge = ({ permission }) => {
    const getPermissionColor = (permission) => {
      if (permission.includes('create')) return 'bg-green-100 text-green-800';
      if (permission.includes('view')) return 'bg-blue-100 text-blue-800';
      if (permission.includes('edit')) return 'bg-yellow-100 text-yellow-800';
      if (permission.includes('delete')) return 'bg-red-100 text-red-800';
      return 'bg-gray-100 text-gray-800';
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getPermissionColor(permission)}`}>
        {permission}
      </span>
    );
  };

  if (loading && isEditMode && !userData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate('/users')}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Retour aux utilisateurs
          </button>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditMode ? 'Modifier l\'Utilisateur' : 'Nouvel Utilisateur'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditMode
              ? 'Mettez à jour les informations de l\'utilisateur'
              : 'Créez un nouveau compte utilisateur'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="p-6">
              <form onSubmit={formik.handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  {/* Username */}
                  <div>
                    <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                      Nom d'utilisateur *
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <input
                          type="text"
                          id="username"
                          name="username"
                          value={formik.values.username}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder="john_doe"
                        />
                      </div>
                      {formik.touched.username && formik.errors.username && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.username}</p>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email *
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder="john@example.com"
                        />
                      </div>
                      {formik.touched.email && formik.errors.email && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
                      )}
                    </div>
                  </div>

                  {/* First Name */}
                  <div>
                    <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                      Prénom *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        value={formik.values.first_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                        placeholder="John"
                      />
                      {formik.touched.first_name && formik.errors.first_name && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.first_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Last Name */}
                  <div>
                    <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                      Nom *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        value={formik.values.last_name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="input-field"
                        placeholder="Doe"
                      />
                      {formik.touched.last_name && formik.errors.last_name && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.last_name}</p>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Téléphone
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <input
                          type="text"
                          id="phone"
                          name="phone"
                          value={formik.values.phone}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder="+221 33 123 45 67"
                        />
                      </div>
                      {formik.touched.phone && formik.errors.phone && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.phone}</p>
                      )}
                    </div>
                  </div>

                  {/* Agency */}
                  <div>
                    <label htmlFor="agency_id" className="block text-sm font-medium text-gray-700">
                      Agence *
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <select
                          id="agency_id"
                          name="agency_id"
                          value={formik.values.agency_id}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                        >
                          <option value="">Sélectionnez une agence</option>
                          {agencies.map(agency => (
                            <option key={agency.id} value={agency.id}>
                              {agency.name} ({agency.code})
                            </option>
                          ))}
                        </select>
                      </div>
                      {formik.touched.agency_id && formik.errors.agency_id && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.agency_id}</p>
                      )}
                    </div>
                  </div>

                  {/* Password (only for new users or when changing) */}
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                      {isEditMode ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <KeyIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <input
                          type="password"
                          id="password"
                          name="password"
                          value={formik.values.password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder={isEditMode ? '••••••••' : '••••••••'}
                        />
                      </div>
                      {formik.touched.password && formik.errors.password && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.password}</p>
                      )}
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
                      {isEditMode ? 'Confirmer le nouveau mot de passe' : 'Confirmer le mot de passe *'}
                    </label>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <KeyIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <input
                          type="password"
                          id="confirm_password"
                          name="confirm_password"
                          value={formik.values.confirm_password}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                          placeholder="••••••••"
                        />
                      </div>
                      {formik.touched.confirm_password && formik.errors.confirm_password && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.confirm_password}</p>
                      )}
                    </div>
                  </div>

                  {/* Role */}
                  <div className="sm:col-span-2">
                    <div className="flex justify-between items-center">
                      <label htmlFor="role_id" className="block text-sm font-medium text-gray-700">
                        Rôle *
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowRoleModal(true)}
                        className="text-sm text-primary-600 hover:text-primary-900"
                      >
                        + Créer un nouveau rôle
                      </button>
                    </div>
                    <div className="mt-1">
                      <div className="flex items-center">
                        <ShieldCheckIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <select
                          id="role_id"
                          name="role_id"
                          value={formik.values.role_id}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          className="input-field flex-1"
                        >
                          <option value="">Sélectionnez un rôle</option>
                          {roles.map(role => (
                            <option key={role.id} value={role.id}>
                              {role.name} - {role.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      {formik.touched.role_id && formik.errors.role_id && (
                        <p className="mt-2 text-sm text-red-600">{formik.errors.role_id}</p>
                      )}
                    </div>
                  </div>

                  {/* Status */}
                  <div className="sm:col-span-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        name="is_active"
                        checked={formik.values.is_active}
                        onChange={formik.handleChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                        Compte actif
                      </label>
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      Les utilisateurs inactifs ne peuvent pas se connecter
                    </p>
                  </div>
                </div>

                {/* Permissions Preview */}
                {formik.values.role_id && permissions.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      Permissions associées à ce rôle
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {permissions.map((permission, index) => (
                        <PermissionBadge key={index} permission={permission} />
                      ))}
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      {permissions.length} permission(s) au total
                    </p>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                    disabled={loading}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading || !formik.isValid}
                  >
                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isEditMode ? 'Mise à jour...' : 'Création...'}
                      </>
                    ) : (
                      isEditMode ? 'Mettre à jour' : 'Créer l\'utilisateur'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="space-y-6">
            {/* Information */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Informations</h3>
                <ul className="space-y-3">
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Date de création</span>
                    <span className="text-sm font-medium text-gray-900">
                      {userData?.created_at 
                        ? new Date(userData.created_at).toLocaleDateString('fr-FR')
                        : new Date().toLocaleDateString('fr-FR')}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Dernière connexion</span>
                    <span className="text-sm font-medium text-gray-900">
                      {userData?.last_login 
                        ? new Date(userData.last_login).toLocaleDateString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </li>
                  <li className="flex justify-between">
                    <span className="text-sm text-gray-600">Statut</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${formik.values.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
                    `}>
                      {formik.values.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </li>
                  {userData?.role_name && (
                    <li className="flex justify-between">
                      <span className="text-sm text-gray-600">Rôle actuel</span>
                      <span className="text-sm font-medium text-gray-900">
                        {userData.role_name}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Tips */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Conseils</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="ml-3 text-sm text-gray-600">
                      Utilisez un nom d'utilisateur unique et facile à retenir
                    </p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="ml-3 text-sm text-gray-600">
                      Attribuez les rôles en fonction des responsabilités
                    </p>
                  </li>
                  <li className="flex items-start">
                    <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <p className="ml-3 text-sm text-gray-600">
                      Activez les comptes après vérification des informations
                    </p>
                  </li>
                </ul>
              </div>
            </div>

            {/* Security Tips */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Sécurité</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="ml-3 text-sm text-gray-600">
                      Ne jamais partager les mots de passe par email
                    </p>
                  </li>
                  <li className="flex items-start">
                    <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="ml-3 text-sm text-gray-600">
                      Utiliser des mots de passe complexes
                    </p>
                  </li>
                  <li className="flex items-start">
                    <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="ml-3 text-sm text-gray-600">
                      Révoquer les accès des utilisateurs partants
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Créer un nouveau rôle
              </h3>
              
              <div className="space-y-4">
                {/* Role Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nom du rôle *
                  </label>
                  <input
                    type="text"
                    value={newRole.name}
                    onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                    className="input-field mt-1"
                    placeholder="Ex: Gestionnaire Agence"
                  />
                </div>

                {/* Role Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    value={newRole.description}
                    onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                    className="input-field mt-1"
                    rows={3}
                    placeholder="Description du rôle..."
                  />
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
                    {availablePermissions.length > 0 ? (
                      <div className="space-y-2">
                        {availablePermissions.map((permission, index) => (
                          <div key={index} className="flex items-center">
                            <input
                              type="checkbox"
                              id={`perm-${index}`}
                              checked={newRole.permissions.includes(permission.name || permission)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewRole({
                                    ...newRole,
                                    permissions: [...newRole.permissions, permission.name || permission]
                                  });
                                } else {
                                  setNewRole({
                                    ...newRole,
                                    permissions: newRole.permissions.filter(p => p !== (permission.name || permission))
                                  });
                                }
                              }}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            />
                            <label htmlFor={`perm-${index}`} className="ml-2 text-sm text-gray-900">
                              {permission.name || permission}
                              {permission.description && (
                                <span className="text-gray-500 ml-2">- {permission.description}</span>
                              )}
                            </label>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Chargement des permissions...</p>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    {newRole.permissions.length} permission(s) sélectionnée(s)
                  </p>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowRoleModal(false);
                    setNewRole({ name: '', description: '', permissions: [] });
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateRole}
                  disabled={!newRole.name || newRole.permissions.length === 0}
                  className="btn-primary"
                >
                  Créer le rôle
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserForm;